import * as SearchUrlParameters from '../models/SearchUrlParameters';
import {
    ARRIVAL,
    DEPARTURE,
    FILTER,
    KEYWORDS,
    MAX_BATHROOMS,
    MAX_BEDROOMS,
    MAX_NIGHTLY_PRICE,
    MAX_TOTAL_PRICE,
    MAX_SLEEPS,
    MIN_BATHROOMS,
    MIN_BEDROOMS,
    MIN_NIGHTLY_PRICE,
    MIN_TOTAL_PRICE,
    MIN_SLEEPS,
    PAGE
} from './SearchUrlParameters';

// @flow
// Region Parameter can be brand-specific.
// Though we are greedy in determining regionId value and do not consider site name.
// See https://github.homeawaycorp.com/geo/gbs/blob/752486c57169c43721a633321591e93d3d419423/gbs-url/src/main/java/com/homeaway/geo/gbs/url/GbsUrlUtils.java#L31
// and https://github.homeawaycorp.com/geo/gbs/blob/752486c57169c43721a633321591e93d3d419423/gbs-url/src/main/java/com/homeaway/geo/gbs/url/GbsUrlService.java#L98
export const REGION_COLON_PARAMETER_VARIANTS: string[] = [
    /* default */
    'region',
    /* vrbo? */
    'regions',
    /* homeaway_de */
    'regionen',
    /* BR */
    'regiao',
    /* IT */
    'regione',
    /* NL */
    'regio',
    /* FI */
    'Matkakohteesi'
];

export const LEGACY_PATH_PARAM_PROPERTY_TYPES: string[] = [
    'property type',
    'unterkunftsart'
];

export const LEGACY_PATH_PARAM_LOCATION_TYPES: string[] = [
    'location type',
    'ortstyp'
];

export const LEGACY_PATH_PARAM_FEATURE_TYPES: string[] = [
    'features',
    'ausstattung'
];

export const BOUNDING_BOX_REGEX: RegExp = /@(-?\d+\.?\d+,){4}(\d{1,2})z/g;

export const LANDING_PAGE_URL_PATTERN: RegExp = /\/(.+)\/r(\d+)/;

/**
 * Supported legacy landing page paths for {@link parseURL}. node-search-url-parser currently does not support
 * generating these legacy URLs with {@link toURL} and can only parse them with {@link parseURL}.
 *
 * @typedef LANDING_PAGE_URL_PREFIXES
 * @type {array}
 * @property {string} 0 - /vacances - abritel, canada french, expedia fr
 * @property {string} 1 - /ferias - alugueTemporada
 * @property {string} 2 - /holiday-rentals - australia
 * @property {string} 3 - /cottage-rentals - canada english
 * @property {string} 4 - /ferie-bolig-lejlighed - denmark
 * @property {string} 5 - /urlaub-ferienwohnung-ferienhaus - FewoDirekt, expedia de
 * @property {string} 6 - /loma-asunto - finland
 * @property {string} 7 - /holiday-accommodation - new zealand
 * @property {string} 8 - /lettings - united kingdom
 * @property {string} 9 - /location-vacances homelidays com (fr)
 * @property {string} 10 - /alquiler-vacacional - homelidays es
 * @property {string} 11 - /case-vacanze - homelidays it
 * @property {string} 12 - /case-vacanze-appartamenti - italy, expedia it
 * @property {string} 13 - /alquileres-vacaciones - mexico
 * @property {string} 14 - /vakantie-vakantiehuis-vakantiewoning - netherlands
 * @property {string} 15 - /ferie-leilighet-feriehus - norway
 * @property {string} 16 - /rentals - odhr
 * @property {string} 17 - /apartamenty-wakacyjne - poland
 * @property {string} 18 - /casas-apartamentos-ferias - portugal
 * @property {string} 19 - /alquiler-vacaciones - spain
 * @property {string} 20 - /semester-stugor-laegenhet - sweden
 * @property {string} 21 - /vacation-rentals - luxury
 * @property {string} 22 - /browse - vacation rentals
 * @property {string} 23 - /ενοικίαση-για-διακοπές - homeaway gr
 * @property {string} 24 - /ferienwohnung-ferienhaus-urlaub - homeaway at
 * @property {string} 25 - /annonces - abritel
 * @property {string} 26 - /accommodation - stayz
 * @property {string} 27 - /baches-and-holiday-homes - bookabach
 */
export const LANDING_PAGE_URL_PREFIXES: string[] = [
    '/vacances',
    '/ferias',
    '/holiday-rentals',
    '/cottage-rentals',
    '/ferie-bolig-lejlighed',
    /* FewoDirekt, expedia de */
    '/urlaub-ferienwohnung-ferienhaus',
    /* finland */
    '/loma-asunto',
    /* new zealand */
    '/holiday-accommodation',
    /* united kingdom */
    '/lettings',
    /* homelidays com (fr) */
    '/location-vacances',
    /* homelidays es */
    '/alquiler-vacacional',
    /* homelidays it */
    '/case-vacanze',
    /* italy, expedia it */
    '/case-vacanze-appartamenti',
    /* mexico */
    '/alquileres-vacaciones',
    /* netherlands */
    '/vakantie-vakantiehuis-vakantiewoning',
    /* norway */
    '/ferie-leilighet-feriehus',
    /* odhr */
    '/rentals',
    /* poland */
    '/apartamenty-wakacyjne',
    /* portugal */
    '/casas-apartamentos-ferias',
    /* spain */
    '/alquiler-vacaciones',
    /* sweden */
    '/semester-stugor-laegenhet',
    /* luxury */
    '/vacation-rentals',
    /* vr */
    '/browse',
    /* homeaway-gr */
    '/ενοικίαση-για-διακοπές',
    /* homeaway-at */
    '/ferienwohnung-ferienhaus-urlaub',
    /* abritel legacy */
    '/annonces',
    /* stayz */
    '/accommodation',
    /* bookabach */
    '/baches-and-holiday-homes'
];

export const SEARCH_URL_PARAMETERS = Object.keys(SearchUrlParameters)
    .filter((k) => k !== '__esModule')
    .map((k) => SearchUrlParameters[k]);

export const SLASH_PARAMETERS: string[] = [
    MIN_NIGHTLY_PRICE,
    MAX_NIGHTLY_PRICE,
    MIN_TOTAL_PRICE,
    MAX_TOTAL_PRICE,
    MIN_BEDROOMS,
    MAX_BEDROOMS,
    MIN_SLEEPS,
    MAX_SLEEPS,
    MIN_BATHROOMS,
    MAX_BATHROOMS,
    FILTER
];

export const COLON_PARAMETERS: string[] = [PAGE, KEYWORDS, ARRIVAL, DEPARTURE];
